import { type FC, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckIcon,
  ChevronDownIcon,
  CircleIcon,
  WandSparkles
} from 'lucide-react';
import { SiClaude, SiGooglegemini, SiOpenai } from 'react-icons/si';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import SubtleBadge from '@/components/common/SubtleBadge';
import { cn } from '@/lib/utils';
import { useMcpChatModel } from './McpChatModelContext';
import {
  McpMenuEmpty,
  McpMenuItem,
  McpMenuList,
  McpMenuSearchHeader,
  McpMenuShell,
  menuStyleAbove,
  useSearchMenuPopover
} from './McpSearchMenu';
import type { McpProjectAiModel } from '../types/mcpApiTypes';

const PROVIDER_META: Record<string, { label: string; color: string }> = {
  google: { label: 'Gemini', color: '#8E83F5' },
  openai: { label: 'GPT', color: '#10A37F' },
  anthropic: { label: 'Claude', color: '#D97706' },
  auto: { label: 'Auto', color: '#6C63FF' }
};

function ProviderIcon({
  provider,
  color,
  size = 16
}: {
  provider: string;
  color: string;
  size?: number;
}) {
  if (provider === 'google') {
    return <SiGooglegemini size={size} color={color} aria-hidden />;
  }
  if (provider === 'openai') {
    return <SiOpenai size={size} color={color} aria-hidden />;
  }
  if (provider === 'anthropic') {
    return <SiClaude size={size} color={color} aria-hidden />;
  }
  if (provider === 'auto') {
    return <WandSparkles size={size} color={color} aria-hidden />;
  }
  return <SiOpenai size={size} color={color} aria-hidden />;
}

function AutoIcon({ size = 16 }: { size?: number }) {
  return <WandSparkles size={size} color="#6C63FF" aria-hidden />;
}

function sortModels(a: McpProjectAiModel, b: McpProjectAiModel) {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  return a.displayName.localeCompare(b.displayName);
}

function filterModels(models: McpProjectAiModel[], query: string) {
  const q = query.trim().toLowerCase();
  const sorted = [...models].sort(sortModels);
  if (!q) return sorted;
  return sorted.filter(m =>
    [
      m.displayName,
      m.modelId,
      m.provider,
      PROVIDER_META[m.provider]?.label,
      ...(m.tags ?? [])
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(q)
  );
}

export const McpModelPicker: FC = () => {
  const { models, selection, selectedModel, isAuto, setSelection, isLoading } =
    useMcpChatModel();

  // Auto is listed as a model (provider 'auto') but stored as `{ type: 'auto' }`.
  const isSelected = (model: McpProjectAiModel) =>
    model.provider === 'auto'
      ? isAuto
      : selection?.type === 'model' && selection.modelId === model.id;

  const selectableModels = useMemo(
    () => models.filter(model => model.isActive),
    [models]
  );

  const menu = useSearchMenuPopover({
    items: selectableModels,
    filter: filterModels,
    isSelected,
    onPick: model =>
      setSelection(
        model.provider === 'auto'
          ? { type: 'auto' }
          : { type: 'model', modelId: model.id }
      )
  });

  const triggerLabel = isAuto
    ? 'Auto'
    : (selectedModel?.displayName ?? 'مدل');

  const triggerMeta = selectedModel
    ? (PROVIDER_META[selectedModel.provider] ?? {
        label: selectedModel.displayName,
        color: '#6c757d'
      })
    : { label: 'Auto', color: '#5b5bd6' };

  return (
    <>
      <TooltipProvider delayDuration={200}>
        <Tooltip open={menu.open ? false : undefined}>
          <TooltipTrigger asChild>
            <button
              ref={menu.triggerRef}
              type="button"
              className={cn(
                'mcp-picker-trigger d-inline-flex align-items-center justify-content-center gap-1 border-0 rounded-pill px-2.5 py-0.5',
                menu.open && 'is-open'
              )}
              disabled={isLoading || selectableModels.length === 0}
              aria-haspopup="listbox"
              aria-expanded={menu.open}
              aria-label={triggerLabel}
              onClick={menu.toggle}
            >
              {isAuto ? (
                <AutoIcon size={12} />
              ) : selection?.type === 'model' ? (
                <ProviderIcon
                  provider={selectedModel?.provider ?? ''}
                  color={triggerMeta.color}
                  size={12}
                />
              ) : (
                <CircleIcon className="size-3 opacity-50" strokeWidth={2} />
              )}
              <span className="text-truncate fw-semibold fs--2" style={{ maxWidth: '8.5rem' }}>
                {triggerLabel}
              </span>
              <ChevronDownIcon
                className={cn(
                  'mcp-picker-chevron flex-shrink-0 size-3 opacity-55',
                  menu.open && 'is-open'
                )}
                strokeWidth={2}
              />
            </button>
          </TooltipTrigger>
          {selectedModel || isAuto ? (
            <TooltipContent
              side="top"
              sideOffset={8}
              className="mcp-picker-tooltip border shadow rounded-3 bg-white dark__bg-card-dark text-body text-start px-3 py-2"
              dir="rtl"
            >
              <div className="fs--1 fw-semibold lh-sm mb-1">
                {isAuto ? 'Auto' : selectedModel?.displayName}
              </div>
              <div className="d-flex flex-wrap gap-1">
                {isAuto ? (
                  <SubtleBadge bg="primary" pill className="fs--2">
                    انتخاب خودکار مدل
                  </SubtleBadge>
                ) : (
                  (selectedModel?.tags ?? []).map(tag => (
                    <SubtleBadge key={tag} bg="primary" pill className="fs--2">
                      {tag}
                    </SubtleBadge>
                  ))
                )}
              </div>
            </TooltipContent>
          ) : null}
        </Tooltip>
      </TooltipProvider>

      {menu.anchor
        ? createPortal(
            <McpMenuShell
              ref={menu.menuRef}
              ariaLabel="انتخاب مدل"
              style={menuStyleAbove(menu.anchor, {
                align: 'left',
                maxHeightCap: 360
              })}
            >
              <McpMenuSearchHeader
                title="مدل‌ها"
                searchAriaLabel="جستجوی مدل"
                search={menu.search}
                onSearchChange={menu.setSearch}
                searchRef={menu.searchRef}
                searchInputProps={{ onKeyDown: menu.onSearchKeyDown }}
              />
              {menu.filtered.length === 0 ? (
                <McpMenuEmpty>
                  {selectableModels.length === 0
                    ? 'مدلی نیست'
                    : 'مدلی پیدا نشد'}
                </McpMenuEmpty>
              ) : (
                <McpMenuList>
                  {menu.filtered.map((model, index) => {
                    const meta = PROVIDER_META[model.provider] ?? {
                      label: model.provider,
                      color: '#6c757d'
                    };
                    const tags = model.tags ?? [];
                    return (
                      <McpMenuItem
                        key={model.id}
                        index={index}
                        active={index === menu.activeIndex}
                        onActiveIndexChange={menu.setActiveIndex}
                        onSelect={() => menu.pick(model)}
                        title={model.displayName}
                      >
                        <span
                          className="d-inline-flex align-items-center gap-1 min-w-0 flex-grow-1"
                          dir="ltr"
                        >
                          <ProviderIcon
                            provider={model.provider}
                            color={meta.color}
                            size={14}
                          />
                          <span
                            className="fs--1 fw-medium text-truncate"
                            style={{
                              // Halfway between gray-600 and gray-700 (theme-aware).
                              color:
                                'color-mix(in srgb, rgb(var(--falcon-gray-600-rgb)), rgb(var(--falcon-gray-700-rgb)))'
                            }}
                          >
                            {model.displayName}
                          </span>
                          {tags.length > 0 ? (
                            <span
                              className="d-inline-flex flex-wrap justify-content-end gap-1 flex-shrink-0"
                              style={{ marginLeft: 'auto' }}
                            >
                              {tags.map(tag => (
                                <SubtleBadge
                                  key={tag}
                                  bg="info"
                                  pill
                                  style={{ fontSize: '0.6rem' }}
                                >
                                  {tag}
                                </SubtleBadge>
                              ))}
                            </span>
                          ) : null}
                        </span>
                        {isSelected(model) ? (
                          <CheckIcon
                            className="text-success flex-shrink-0"
                            size={12}
                            strokeWidth={2.5}
                          />
                        ) : null}
                      </McpMenuItem>
                    );
                  })}
                </McpMenuList>
              )}
            </McpMenuShell>,
            document.body
          )
        : null}
    </>
  );
};
